import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Radio,
  RadioGroup,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { RootState } from '../../../utils/redux';
import { FormField } from '../../../utils/redux/slices/webformSlice';
import NoDataFallback from '../../common/NoDataFallback';
import theme from '../../../theme';

const WebFormPreview = ({
  handleSubmitApi,
  isPending,
  isEditing,
}: {
  handleSubmitApi: (data: Record<string, any>) => void;
  isPending: boolean;
  isEditing: boolean;
}) => {
  const { formConfig, formFields } = useSelector(
    (state: RootState) => state.webform
  );

  // State to track form values
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  // State to track form validity
  const [isFormValid, setIsFormValid] = useState(false);
  // State to track password visibility
  const [passwordVisibility, setPasswordVisibility] = useState<
    Record<string, boolean>
  >({});

  // Validate form whenever values change
  useEffect(() => {
    // Check if all required fields have values
    const requiredFieldsFilled = formFields
      .filter(field => field.required)
      .every(field => {
        const value = formValues[field.name];
        if (value === undefined || value === null || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });

    setIsFormValid(requiredFieldsFilled);
  }, [formValues, formFields]);

  const handleChange = (fieldName: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const togglePasswordVisibility = (fieldName: string) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      handleSubmitApi(formValues);
    }
  };

  const renderFormField = (field: FormField, index: number) => {
    switch (field.type) {
      case 'text':
      case 'number':
      case 'email':
      case 'date':
        return (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', mb: 2, gap: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#302B3F',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {field.label}
              {field.required && (
                <Typography
                  component="span"
                  sx={{ color: '#EF4444', ml: 0.5, fontSize: '14px' }}
                >
                  *
                </Typography>
              )}
            </Typography>
            <TextField
              type={field.type}
              placeholder={field.placeholder}
              fullWidth
              value={formValues[field.name] || ''}
              onChange={e => handleChange(field.name, e.target.value)}
              sx={{
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>
        );
      case 'password':
        return (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', mb: 2, gap: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#302B3F',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {field.label}
              {field.required && (
                <Typography
                  component="span"
                  sx={{ color: '#EF4444', ml: 0.5, fontSize: '14px' }}
                >
                  *
                </Typography>
              )}
            </Typography>
            <TextField
              type={passwordVisibility[field.name] ? 'text' : 'password'}
              placeholder={field.placeholder}
              fullWidth
              value={formValues[field.name] || ''}
              onChange={e => handleChange(field.name, e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility(field.name)}
                      edge="end"
                      size="small"
                    >
                      {passwordVisibility[field.name] ? (
                        <EyeSlash size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>
        );
      case 'select':
        return (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', mb: 2, gap: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#302B3F',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {field.label}
              {field.required && (
                <Typography
                  component="span"
                  sx={{ color: '#EF4444', ml: 0.5, fontSize: '14px' }}
                >
                  *
                </Typography>
              )}
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                displayEmpty
                value={formValues[field.name] || ''}
                onChange={e => handleChange(field.name, e.target.value)}
                renderValue={(selected: any) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: '#6B6F80', fontSize: '14px' }}>
                        {field.placeholder || 'Select an option'}
                      </Typography>
                    );
                  }
                  return selected;
                }}
                sx={{
                  height: '40px',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  background: '#F8F9FC',
                  '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    {
                      border: 'none',
                    },
                  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    {
                      border: 'none',
                    },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '8px 12px',
                    paddingRight: '26px',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid #DCE0E7',
                    background: '#F8F9FC',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiSelect-placeholder': {
                    fontSize: '12px',
                  },
                }}
              >
                {field.options?.map((option, optionIndex) => (
                  <MenuItem
                    key={optionIndex}
                    value={option}
                    sx={{ fontSize: '16px' }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      case 'radio':
        return (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', mb: 2, gap: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#302B3F',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {field.label}
              {field.required && (
                <Typography
                  component="span"
                  sx={{ color: '#EF4444', ml: 0.5, fontSize: '14px' }}
                >
                  *
                </Typography>
              )}
            </Typography>
            <RadioGroup
              value={formValues[field.name] || ''}
              onChange={e => handleChange(field.name, e.target.value)}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              {field.options?.map((option, optionIndex) => (
                <FormControlLabel
                  key={optionIndex}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: '#DCE0E7',
                        '&.Mui-checked': {
                          color: '#6366F1',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#302B3F',
                        fontWeight: 400,
                      }}
                    >
                      {option}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        );
      case 'checkbox':
        return (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', mb: 2, gap: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#302B3F',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {field.label}
              {field.required && (
                <Typography
                  component="span"
                  sx={{ color: '#EF4444', ml: 0.5, fontSize: '14px' }}
                >
                  *
                </Typography>
              )}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {field.options?.map((option, optionIndex) => {
                // Initialize an array for checkboxes if it doesn't exist
                const selectedOptions = Array.isArray(formValues[field.name])
                  ? formValues[field.name]
                  : [];

                const isChecked = selectedOptions.includes(option);

                return (
                  <FormControlLabel
                    key={optionIndex}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={e => {
                          if (e.target.checked) {
                            // Add to selected options
                            handleChange(field.name, [
                              ...selectedOptions,
                              option,
                            ]);
                          } else {
                            // Remove from selected options
                            handleChange(
                              field.name,
                              selectedOptions.filter(
                                (opt: string) => opt !== option
                              )
                            );
                          }
                        }}
                        sx={{
                          marginLeft: '8px',
                          width: '18px',
                          height: '18px',
                          color: 'transparent',
                          '&.Mui-checked': {
                            color: '#6366F1',
                          },
                          '& .MuiSvgIcon-root': {
                            borderRadius: '6px',
                            border: '1px solid #DCE0E7',
                            backgroundColor: '#FFFFFF',
                            width: '18px',
                            height: '18px',
                            '&:hover': {
                              borderColor: '#6366F1',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#302B3F',
                          fontWeight: 400,
                          marginLeft: '8px',
                        }}
                      >
                        {option}
                      </Typography>
                    }
                    sx={{
                      marginLeft: '-11px',
                      '& .MuiFormControlLabel-label': {
                        display: 'flex',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  // If not in editing mode, show the NoDataFallback component
  if (!isEditing) {
    return (
      <NoDataFallback
        message="Preview Not Available"
        description="To see the preview, please save the function first."
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        width: '100%',
        maxWidth: '700px',
        mx: 'auto',
        borderRadius: '0px',
        boxShadow: 'none',
      }}
    >
      {/* Form Header */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '24px',
          color: '#302B3F',
        }}
      >
        {formConfig.formTitle || 'Form Preview'}
      </Typography>

      {formConfig.formDescription && (
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '12px',
            color: '#6B6F80',
            mb: 4,
          }}
        >
          {formConfig.formDescription}
        </Typography>
      )}

      {/* Form Fields */}
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        {formFields.length > 0 ? (
          formFields.map((field, index) => renderFormField(field, index))
        ) : (
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#6B6F80',
              textAlign: 'center',
              my: 4,
              fontSize: '16px',
            }}
          >
            No form fields added yet. Add fields in the builder to see preview.
          </Typography>
        )}

        {formFields.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid || isPending}
              sx={{
                mt: 2,
                mb: 1,
                backgroundColor: '#6366F1',
                width: 'fit-content',
                height: '34px',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                boxShadow: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#4F46E5',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#DCE0E7',
                  color: '#6B6F80',
                },
              }}
            >
              {isPending ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {formConfig.submitButtonText || 'Submit'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WebFormPreview;
